import { useState } from "react";
import { useApiQuery } from "../../../../infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../dialogs/AppConfirmDialog";
import { dateUtility } from "../../../../infrastructure/utilities/dateUtility";
import { IMyWorkoutListItemViewModel } from "../../../../../components/portal/myWorkouts/models/myWorkoutsViewModel";
import { IWorkoutPlanViewModel } from "../../../workoutPlanDialog/models/workoutPlanViewModel";

export interface ISelectedWorkout {
  eventId?: number;
  planId?: number;
  dateLabel: string;
}

/** Staff flow: the new workout is created for `userId` through the staff endpoint. */
export interface ICreateWorkoutForUser {
  endpoint: string;
  userId: number;
}

const NO_SUBSCRIPTION_MESSAGE =
  "Нямате активен абонамент и нямате достъп до тази функционалност.";

/** Local date+time → `YYYY-MM-DDTHH:mm:ss` (no timezone shift — sends the wall-clock the user picked). */
const toApiDateTime = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Owns the workout-list query, the "which workout is open" state, and the standalone
 * create flow for the reusable WorkoutsOverview. The list endpoint/queryKey are injected
 * so the same panel serves both the user's own "My Workouts" page and the admin
 * user-profile page. Without `createForUser` the create flow is the client's own
 * (CreateStandalone, subscribed users only); with it, staff create the workout for that
 * user through the staff endpoint.
 */
export const useWorkoutsOverview = (
  listEndpoint: string,
  listQueryKey: unknown[],
  createForUser?: ICreateWorkoutForUser,
) => {
  const { data: workouts, isLoading } = useApiQuery<
    IMyWorkoutListItemViewModel[]
  >({
    queryKey: listQueryKey,
    endpoint: listEndpoint,
    globalIsLoading: false,
  });

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selected, setSelected] = useState<ISelectedWorkout | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { mutateAsync } = useApiMutation<IWorkoutPlanViewModel>();

  // The "+" button is always visible but only opens the create flow when the caller may
  // create (a client needs an active subscription; staff always may); otherwise it
  // surfaces the shared info popup.
  const requestCreate = (canCreate: boolean) => {
    if (!canCreate) {
      showCustomConfirmDialog(
        NO_SUBSCRIPTION_MESSAGE,
        undefined,
        undefined,
        "warning",
        "Разбрах",
        null,
      );
      return;
    }
    setIsCreateOpen(true);
  };

  const confirmCreate = async (date: Date) => {
    setIsCreating(true);
    try {
      const created = (await mutateAsync({
        endpoint:
          createForUser?.endpoint ??
          endpointsMetadata.workoutPlan.createStandalone,
        method: "post",
        payload: {
          workoutDate: toApiDateTime(date),
          ...(createForUser ? { userId: createForUser.userId } : {}),
        },
        // Refresh whichever list this panel shows (the caller's own or the profile's user's).
        invalidateKeys: [listQueryKey],
        disableSuccessToast: true,
        disable401Redirect: true,
      })) as IWorkoutPlanViewModel;

      setIsCreateOpen(false);
      // Open the brand-new workout straight away (by plan id — it has no event).
      setSelected({
        planId: created.id,
        dateLabel: dateUtility.formatDateTime(date),
      });
    } catch {
      // The mutation surfaces its own error toast; nothing more to do here.
    } finally {
      setIsCreating(false);
    }
  };

  return {
    workouts,
    isLoading,
    activeTabIndex,
    setActiveTabIndex,
    selected,
    setSelected,
    isCreateOpen,
    setIsCreateOpen,
    isCreating,
    requestCreate,
    confirmCreate,
  };
};
